import { useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { Button, MinusIcon, PlusIcon } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { BuyPremiumIcon } from '@root/entities/premium/ui/premium-icon'
import { AmountInputBase } from '@root/shared/ui-kit'

import { usePaymentBoostersForm } from '../../common/use-payment-boosters-form'

import type { PurchaseBoostersReatom } from '@root/features/premium'
import type { PremiumApi } from '@root/shared/api'

type Props = {
  model: PurchaseBoostersReatom
  type: PremiumApi.PurchaseTypeDto | 'extend'
  quickOptions?: Array<number>
}

export const PaymentDialogFormFields: React.FC<Props> = ({
  type,
  model,
  quickOptions,
}) => {
  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)
  const {
    boosters,
    boostersRaw,
    boostersPrice,
    setBoostersRaw,
    handleValueClick,
    miniumBoosters,
  } = usePaymentBoostersForm({
    model,
    type,
  })

  return (
    <Root>
      {quickOptions ? (
        <ButtonGroup>
          {quickOptions.map((value) => (
            <ButtonValue
              key={value}
              size="xs"
              type="button"
              radius="rounded"
              variant="secondary"
              $active={boosters.eq(value)}
              onClick={() => setBoostersRaw(value.toString())}
            >
              {value}
            </ButtonValue>
          ))}
        </ButtonGroup>
      ) : undefined}
      <InputGroup>
        <ButtonModify
          variant="soft"
          radius="rounded"
          type="button"
          size="m"
          onClick={() => handleValueClick(-1)}
          disabled={boosters.lt(miniumBoosters)}
          iconLeft={<MinusIcon />}
        />
        <InputHolder>
          <BuyPremiumIcon product="boosters" size={16} sizeIcon={12} />
          <Input
            inputMode="numeric"
            value={boostersRaw}
            min={miniumBoosters}
            onValueChange={({ value }) => {
              setBoostersRaw(value)
            }}
          />
          <Amount>
            <Numeric
              value={boostersPrice}
              preset="currency-amount-short"
              currency={nativeCurrency.ticker}
            />
          </Amount>
        </InputHolder>
        <ButtonModify
          variant="soft"
          radius="rounded"
          type="button"
          size="m"
          onClick={() => handleValueClick(+1)}
          iconLeft={<PlusIcon />}
        />
      </InputGroup>
    </Root>
  )
}

const Root = styled.div`
  display: grid;

  gap: 8px;
`

const InputGroup = styled.div`
  background: var(--appearance-1);
  border-radius: 10px;

  display: grid;
  grid-template-columns: auto 1fr auto;

  padding: 2px;
  height: 40px;
`

const ButtonGroup = styled.div`
  gap: 4px;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
`

const InputHolder = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr auto;

  padding-inline: 12px;
  gap: 6px;
`

const Input = styled(AmountInputBase)`
  background: transparent;
  color: var(--text-12);

  text-align: left;
  font-style: normal;

  border: 0;
  font-size: 13px;
  font-weight: 600;

  width: 100%;
  height: 100%;
`

const Amount = styled.span`
  color: var(--text-9);
  text-align: center;

  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  white-space: nowrap;
`

const ButtonModify = styled(Button)`
  background: var(--backgroundA-3);

  height: 100%;
  width: 24px;

  svg {
    color: var(--textA-7);
  }

  &:hover:not(:disabled) {
    background: var(--backgroundA-4);

    svg {
      color: var(--text-11);
    }
  }
`

const ButtonValue = styled(Button)<{ $active: boolean }>`
  color: var(--text-11);
  border: 1px solid var(--backgroundA-4);

  border-radius: 8px;

  ${({ $active }) =>
    $active &&
    css`
      color: var(--text-12);
      background: var(--backgroundA-3);
    `}

  &:hover {
    color: var(--text-12);
    background: var(--backgroundA-3);
  }
`
